import { Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { isUndefined } from 'lodash';
import { AnnotationState } from '../../../cross-team-shared/annotation/shared/state/annotation-state';
import { isShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { defaultAnnotationColor } from '../../../cross-team-shared/constants';
import { TaxonomyItem } from '../../../team-apis/core/taxonomy';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { isClassTaxonomy } from './taxonomy-loading.service';

@Injectable()
export class ObjectClassService {
  constructor(private taskWorkspaceStore: TaskWorkspaceStore) {}

  getClassLabel(className?: string) {
    return this.getClass(className)?.label;
  }

  getClassPathLabel(className?: string) {
    const objectClass = this.getClass(className);
    if (!objectClass) {
      return undefined;
    }
    const pathIds = objectClass.path;
    const { objectTaxonomy } = this.taskWorkspaceStore.state().taxonomy;
    const pathLabels: string[] = [];

    let children = objectTaxonomy;
    for (const id of pathIds) {
      const item = children.find((i) => i.__id === id);

      if (!item) {
        return undefined;
      }

      if (!isClassTaxonomy(item)) {
        children = item.children;
      }

      pathLabels.push(item.label);
    }

    return pathLabels;
  }

  getClassAttributes(className?: string) {
    return this.getClass(className)?.attributes;
  }

  isValidClass(className?: string): className is string {
    const objectClass = this.getClass(className);
    return isDefined(objectClass);
  }

  getClass(className?: string) {
    if (isUndefined(className)) {
      return undefined;
    }
    const attributesByClass =
      this.taskWorkspaceStore.state().taxonomy.attributesByClass;
    return attributesByClass[className];
  }

  getClassName(annotation: AnnotationState) {
    const objectId = isShapeState(annotation)
      ? annotation.parentId!
      : annotation.index;

    return this.taskWorkspaceStore.getObjectStore(objectId).state().class;
  }

  getClassColor(className?: string) {
    if (!this.isValidClass(className)) {
      return defaultAnnotationColor;
    }

    const taxonomyItem = getTaxonomyItem(
      this.taskWorkspaceStore.state().taxonomy.objectTaxonomy,
      className,
    );

    assert(taxonomyItem, 'taxonomy item must be defined');
    return taxonomyItem.color || defaultAnnotationColor;
  }
}

function getTaxonomyItem(
  objectTaxonomy: TaxonomyItem[],
  className: string,
): TaxonomyItem | undefined {
  for (const item of objectTaxonomy) {
    if (isClassTaxonomy(item)) {
      if (item.name === className) {
        return item;
      }
      continue;
    }

    const found = getTaxonomyItem(item.children, className);
    if (found) {
      return found;
    }
  }

  return;
}
